import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import ClientsMarquee from "@/components/ClientsMarquee";
import GradientStrip from "@/components/GradientStrip";
import SEO, { organizationJsonLd, websiteJsonLd } from "@/components/SEO";
import { IMG } from "@/lib/config";
import { useT } from "@/lib/i18n";
import { ArrowUpRight, ArrowRight, Compass, Users, Globe2, TrendingUp } from "lucide-react";

const PILLAR_ICONS = [TrendingUp, Users, Compass, Globe2];
const PILLAR_COLORS = ["#EEE316", "#F3A114", "#9DBC1D", "#E62C86"];
const OFFER_PATHS = ["/dirigeants-codir", "/coo-conseil", "/management-leadership", "/ecole-management"];
const OFFER_IMGS = [IMG.codir, IMG.coo, IMG.management, IMG.ecole];
const OFFER_COLORS = ["#E62C86", "#9DBC1D", "#F3A114", "#EEE316"];
const FIG_COLORS = ["#E62C86", "#9DBC1D", "#F3A114"];

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [organizationJsonLd, websiteJsonLd],
};

export default function Home() {
  const t = useT();
  const pillars = t("home.pillars.items", []) || [];
  const offers = t("home.offers.items", []) || [];
  const figures = t("home.figures.items", []) || [];

  return (
    <Layout headerVariant="dark">
      <SEO
        title="WeDo Coaching & Conseil · make it matter"
        description="Cabinet premium pour dirigeants, CODIR, managers et organisations. Leadership, management, amélioration de la communication, gestion de crise, management de transition, structuration et transformation. Interventions FR · EN."
        path="/"
        jsonLd={HOME_JSONLD}
      />
      {/* HERO */}
      <section data-testid="home-hero" className="relative min-h-[100svh] grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-deep/85 via-brand/70 to-brand-deep/85" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-40 md:pt-48 pb-24 min-h-[100svh] flex flex-col justify-between">
          <div>
            <p className="section-num text-white/80 fade-up">{t("home.hero.eyebrow")}</p>
            <h1 className="display text-white text-[3rem] sm:text-[4rem] md:text-[5.5rem] lg:text-[7rem] leading-[0.95] tracking-tight mt-8 max-w-6xl fade-up-2">
              {t("home.hero.line1")}<br/>
              <span className="opacity-95">{t("home.hero.line2")}</span><br/>
              <span>{t("home.hero.line3a")}<span className="text-accent-pink">{t("home.hero.line3b")}</span></span>
            </h1>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-20 md:mt-0 fade-up-3">
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-xl">
                {t("home.hero.lede")}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-pill bg-white text-brand hover:bg-paper-2">
                  {t("common.cta.contact")} <ArrowRight size={16}/>
                </Link>
                <a
                  href="https://diagnostic.wedocoaching.com" target="_blank" rel="noreferrer"
                  data-testid="hero-cta-diagnostic"
                  className="btn-pill bg-accent-pink text-white hover:bg-white hover:text-brand"
                >
                  {t("common.cta.diagnostic")} <ArrowUpRight size={16}/>
                </a>
              </div>
            </div>
          </div>
          <div className="absolute right-6 md:right-12 lg:right-16 top-32 hidden md:block fade-up-4">
            <p className="display uppercase tracking-[0.4em] text-accent-pink/80 text-xs rotate-90 origin-top-right">
              make it matter.
            </p>
          </div>
        </div>
        <GradientStrip />
      </section>

      {/* PILLARS */}
      <section data-testid="home-pillars" className="bg-brand-deep text-white py-28 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="/brand/gradient_wedo.jpg" alt="" className="w-full h-full object-cover mix-blend-overlay" />
        </div>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-20">
            <div className="md:col-span-7">
              <p className="section-num text-accent-pink">{t("home.pillars.eyebrow")}</p>
              <h2 className="display text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-6">
                {t("home.pillars.titleA")}<br/>
                <span className="text-accent-yellow">{t("home.pillars.titleB")}</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pt-6">
              <p className="text-white/85 text-base md:text-lg leading-relaxed">
                {t("home.pillars.lede")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/15">
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              const color = PILLAR_COLORS[i];
              return (
                <div
                  key={p.label}
                  className="bg-brand-deep p-8 md:p-10 flex flex-col gap-6 min-h-[280px] group hover:bg-brand transition-colors duration-500"
                  data-testid={`pillar-${i}`}
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-110" style={{ background: color }}>
                    <Icon size={22} className="text-brand-deep" strokeWidth={2.2}/>
                  </div>
                  <div className="flex-1">
                    <h3 className="display text-2xl md:text-3xl leading-tight">{p.label}</h3>
                    <p className="text-white/90 text-sm leading-relaxed mt-3">{p.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <GradientStrip />

      {/* KEY FIGURES */}
      <section data-testid="home-figures" className="bg-paper py-24 md:py-32 border-b border-ink/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-7">
              <p className="section-num text-accent-pink">{t("home.figures.eyebrow")}</p>
              <h2 className="display text-4xl md:text-6xl leading-[1.02] mt-6">
                {t("home.figures.title")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10">
            {figures.map((k, i) => (
              <div key={k.label} data-testid={`figure-${k.num}`} className="bg-paper p-10 md:p-14 flex flex-col gap-6 group">
                <div className="flex items-baseline gap-3">
                  <span className="display text-7xl md:text-8xl lg:text-9xl leading-none" style={{ color: FIG_COLORS[i] }}>{k.num}</span>
                  <span className="display text-2xl md:text-3xl text-ink/70">{k.suffix}</span>
                </div>
                <div className="h-px w-12 transition-all duration-500 group-hover:w-24" style={{ background: FIG_COLORS[i] }} />
                <p className="text-ink/75 text-[15px] md:text-base leading-relaxed max-w-xs">{k.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GradientStrip />

      {/* OFFERS */}
      <section data-testid="home-offers" className="bg-paper py-28 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-20">
            <div>
              <p className="section-num">{t("home.offers.eyebrow")}</p>
              <h2 className="display text-4xl md:text-6xl leading-tight mt-6 max-w-3xl">
                {t("home.offers.titleA")}<span className="text-accent-pink">{t("home.offers.titleAccent1")}</span><br/>
                {t("home.offers.titleB")}<span className="text-accent-lime">{t("home.offers.titleAccent2")}</span>.
              </h2>
            </div>
            <Link to="/contact" className="btn-pill btn-primary shrink-0">
              {t("home.offers.cta")} <ArrowUpRight size={16}/>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {offers.map((o, i) => {
              const path = OFFER_PATHS[i];
              const img = OFFER_IMGS[i];
              const color = OFFER_COLORS[i];
              return (
                <Link
                  key={path}
                  to={path}
                  data-testid={`offer-${path.replace(/\//g, '')}`}
                  className="group relative bg-brand-deep text-white p-10 md:p-14 hover:bg-brand transition-all duration-500 min-h-[460px] flex flex-col justify-between overflow-hidden"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1.5 transition-all duration-500 group-hover:w-3" style={{ background: color }} />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-25 transition-opacity duration-700 ease-out">
                    <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-deep via-brand-deep/40 to-transparent"/>
                  </div>
                  <div className="relative">
                    <p className="section-num" style={{ color }}>{o.tag}</p>
                    <h3 className="display text-3xl md:text-4xl mt-6 leading-tight max-w-md">{o.title}</h3>
                  </div>
                  <div className="relative flex items-end justify-between">
                    <p className="text-white/80 text-[15px] leading-relaxed max-w-lg">{o.desc}</p>
                    <ArrowUpRight className="shrink-0 ml-6 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" size={28} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <GradientStrip />

      <ClientsMarquee />

      <GradientStrip />

      {/* WILLIAM */}
      <section data-testid="home-william" className="bg-paper py-28 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="relative overflow-hidden">
              <img src={IMG.portrait} alt="William Marques, fondateur de Wedo" className="w-full h-[520px] object-cover" />
              <div className="absolute bottom-6 left-6 right-6 bg-paper p-5">
                <p className="section-num">{t("home.william.caption")}</p>
                <p className="display text-lg mt-2">{t("home.william.role")}</p>
              </div>
              <div className="absolute top-0 left-0 right-0 h-1.5 gradient-bar" />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-16">
            <p className="section-num">{t("home.william.eyebrow")}</p>
            <h2 className="display text-4xl md:text-6xl leading-tight mt-6">
              {t("home.william.titleA")}<br/><span className="text-accent-pink">{t("home.william.titleB")}</span>
            </h2>
            <div className="space-y-5 mt-8 text-[16px] leading-relaxed text-ink/85 max-w-2xl">
              <p>{t("home.william.p1")}</p>
              <p>{t("home.william.p2")}</p>
            </div>
            <Link to="/a-propos" data-testid="william-cta" className="btn-pill btn-ghost mt-10">
              {t("home.william.cta")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="home-manifesto" className="relative text-white py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/brand/gradient_wedo.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-deep/80" />
        </div>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <p className="section-num text-white/80">{t("home.manifesto.eyebrow")}</p>
          <h2 className="display text-4xl md:text-7xl leading-[1.05] mt-8 max-w-5xl">
            {t("home.manifesto.title")}
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <p className="text-white/90 text-lg leading-relaxed max-w-2xl">{t("home.manifesto.lede")}</p>
            </div>
            <div className="md:col-span-5 md:text-right flex flex-wrap gap-4 md:justify-end">
              <a href="https://diagnostic.wedocoaching.com" target="_blank" rel="noreferrer" data-testid="manifesto-diagnostic" className="btn-pill bg-accent-pink text-white hover:bg-white hover:text-brand">
                {t("common.cta.diagnostic")} <ArrowUpRight size={16}/>
              </a>
              <Link to="/contact" data-testid="manifesto-cta" className="btn-pill bg-white text-brand hover:bg-paper-2">
                {t("home.manifesto.ctaConversation")} <ArrowUpRight size={16}/>
              </Link>
            </div>
          </div>
        </div>
      </section>
      <GradientStrip height="h-2"/>
    </Layout>
  );
}
